// Entry point for the I Hear Ewe web app.
// Phase 1: bootstraps a minimal "hello world" shell.
// Later phases will wire up the sound manager, animation manager, grid, etc.

import { initApp } from './app.js';
import { registerServiceWorker } from './pwa-register.js';
import { getLocale, hasStoredLocale, setLocale } from './settings.js';
import { setLocale as setI18nLocale, detectBrowserLocale } from './i18n.js';

function bootstrap() {
  const root = document.getElementById('app');
  if (!root) {
    console.error('[I Hear Ewe] #app element not found');
    return;
  }

  // iOS Safari `:active` / tap-awareness workaround.
  //
  // Historically, iOS Safari refuses to apply `:active` styles or
  // reliably fire click/tap events on elements unless the document
  // has at least one `touchstart` listener registered. While our
  // splash overlay was on screen it had one, so tap recognition
  // worked — but after the splash dismissed and was removed from the
  // DOM, the document lost its only touchstart listener and grid
  // buttons stopped responding on device (even though desktop was
  // fine). Adding a permanent no-op listener on the document keeps
  // iOS in "touch-aware" mode for the life of the session.
  //
  // The listener is passive (doesn't call preventDefault) so it
  // can't interfere with scrolling or tap/swipe disambiguation.
  document.addEventListener('touchstart', () => {}, { passive: true });

  // On the very first visit (no saved preference), detect the
  // browser's preferred language and persist it so subsequent
  // page loads use the same locale without re-detecting.
  if (!hasStoredLocale()) {
    const detected = detectBrowserLocale();
    setLocale(detected);          // persist to localStorage
  }

  // Sync the persisted locale into the i18n runtime before the app
  // renders any translated strings. If the stored locale isn't
  // recognised (e.g. it was removed in a build), setI18nLocale
  // returns false and i18n stays on its default (English).
  setI18nLocale(getLocale());

  initApp(root);

  // Register the service worker AFTER the app has mounted. SW setup
  // must never block first paint — the grid has to render even if
  // the SW registration hangs, fails, or the browser doesn't support
  // service workers at all. registerServiceWorker() itself swallows
  // errors and no-ops in environments without the API; see
  // pwa-register.js for the rationale.
  registerServiceWorker();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
